// SNIGJ - Home Page with Thematic Filters and Widgets
import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppearance } from '../context/AppearanceContext';
import { t } from '../lib/i18n';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, FileText, Users, FolderOpen, CreditCard, Shield, Scale, Settings,
  Globe, UserCircle, FileBarChart, AlertTriangle, DollarSign, Languages, Palette,
  Building2, Gavel, Inbox, Send, Wallet, PieChart, BarChart3, Calculator,
  Clock, Bell, Calendar, MapPin, Briefcase, FileCheck, Lock, UserPlus, Maximize2, Minimize2,
  TrendingUp, Activity, UserSearch, HandMetal, BookOpen, Plane, Swords, Home, Phone,
  Monitor, Smartphone, Tablet, Wifi
} from 'lucide-react';
import api from '../lib/api';
import PoliceAssistant from '../components/assistant/PoliceAssistant';
import CallCenterWidget from '../components/CallCenterWidget';

// Thematic Filters Configuration - each filter maps to a module_access code
const THEMATIC_FILTERS = [
  { 
    id: 'all', 
    label: { fr: 'Tout', en: 'All' }, 
    icon: Home, 
    color: 'bg-slate-600',
    module: null, // Always visible
    description: { fr: 'Afficher toutes les données', en: 'Show all data' }
  },
  { 
    id: 'civil', 
    label: { fr: 'Justice Civile', en: 'Civil Justice' }, 
    icon: Scale, 
    color: 'bg-blue-600',
    module: 'justice_civile',
    description: { fr: 'Tribunaux civils, affaires civiles', en: 'Civil courts, civil cases' }
  },
  { 
    id: 'military', 
    label: { fr: 'Justice Militaire', en: 'Military Justice' }, 
    icon: Swords, 
    color: 'bg-red-600',
    module: 'justice_militaire',
    description: { fr: 'Tribunaux militaires, justice des FARDC', en: 'Military tribunals, FARDC justice' }
  },
  { 
    id: 'prison', 
    label: { fr: 'Prisons', en: 'Prisons' }, 
    icon: Lock, 
    color: 'bg-orange-600',
    module: 'prisons',
    description: { fr: 'Gestion pénitentiaire, détenus', en: 'Prison management, inmates' }
  },
  { 
    id: 'finance', 
    label: { fr: 'Finances', en: 'Finances' }, 
    icon: Wallet, 
    color: 'bg-emerald-600',
    module: 'finances',
    description: { fr: 'Caisse, paiements, comptabilité', en: 'Cash register, payments, accounting' }
  },
  { 
    id: 'border', 
    label: { fr: 'Frontières', en: 'Borders' }, 
    icon: Plane, 
    color: 'bg-purple-600',
    module: 'frontieres',
    description: { fr: 'Contrôle aux frontières, alertes', en: 'Border control, alerts' }
  },
  { 
    id: 'library', 
    label: { fr: 'Bibliothèque', en: 'Library' }, 
    icon: BookOpen, 
    color: 'bg-green-600',
    module: 'documents',
    description: { fr: 'Codes, lois, documentation', en: 'Codes, laws, documentation' }
  }
];

const HomePage = () => {
  const { user, language } = useAuth();
  const { settings: appearanceSettings } = useAppearance();
  const [activeFilter, setActiveFilter] = useState('all');
  const [stats, setStats] = useState({
    // Civil Justice
    civilCases: 0,
    civilTrials: 0,
    civilComplaints: 0,
    // Military Justice
    militaryCases: 0,
    militaryTrials: 0,
    // Prison
    totalPrisoners: 0,
    preventivePrisoners: 0,
    condemnedPrisoners: 0,
    // Border
    borderAlerts: 0,
    fugitives: 0,
    // General
    totalCases: 0,
    openCases: 0,
    totalComplaints: 0,
    pendingComplaints: 0,
    totalPayments: 0,
    notifications: 0,
    totalTrials: 0
  });
  const [loading, setLoading] = useState(true);
  const [showPoliceAssistant, setShowPoliceAssistant] = useState(false);
  const [showCallCenter, setShowCallCenter] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  
  const isPoliceRole = ['AGENT_POLICE', 'POLICE_OFFICER', 'OPJ', 'COMMISSAIRE_ADJOINT', 'COMMISSAIRE_PRINCIPAL'].includes(user?.role);
  const isCitizen = user?.role === 'CITIZEN';
  const isAdmin = ['NATIONAL_ADMIN', 'TECH_SUPPORT', 'MINISTER', 'MINISTRE_JUSTICE', 'PRESIDENT', 'PREMIER_MINISTRE', 'SUPER_ADMIN'].includes(user?.role);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // Module access check - same logic as DashboardLayout sidebar
  const userModules = user?.module_access || [];
  const hasModuleConfig = userModules.length > 0;
  const hasAccess = (moduleCode) => {
    if (!moduleCode) return true; // null module = always visible (e.g. "all" filter)
    if (isSuperAdmin || isAdmin) return true;
    if (!hasModuleConfig) return true; // backward compat: no config = show all
    return userModules.includes(moduleCode);
  };

  // Map widget categories to module codes
  const categoryModuleMap = {
    civil: 'justice_civile',
    military: 'justice_militaire',
    prison: 'prisons',
    border: 'frontieres',
    library: 'documents',
    finance: 'finances',
    general: null, // always visible
  };

  // Filter thematic filters based on module access
  const accessibleFilters = THEMATIC_FILTERS.filter(f => hasAccess(f.module));

  useEffect(() => {
    if (isCitizen) return;
    loadStats();
    loadLoginHistory();
    if (isPoliceRole) {
      const hasSeenAssistant = localStorage.getItem('snigj_police_assistant_seen');
      if (!hasSeenAssistant) {
        setShowPoliceAssistant(true);
        localStorage.setItem('snigj_police_assistant_seen', 'true');
      }
    }
  }, [isPoliceRole, isCitizen]);

  // Redirect citizens directly to their dashboard
  if (isCitizen) {
    return <Navigate to="/dashboard" replace />;
  }

  const loadLoginHistory = async () => {
    try {
      const res = await api.get('/auth/login-history', { params: { limit: 7 } });
      setLoginHistory(res.data.history || []);
    } catch (err) {
      console.error('Failed to load login history:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter widgets based on active filter AND module access
  const getFilteredWidgets = () => {
    const allWidgets = [
      // Civil Justice Widgets
      {
        id: 'civil-cases',
        category: 'civil',
        title: language === 'fr' ? 'Dossiers Civils' : 'Civil Cases',
        value: stats.civil_cases || 0,
        icon: Briefcase,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        link: '/cases?type=civil'
      },
      {
        id: 'civil-trials',
        category: 'civil',
        title: language === 'fr' ? 'Procès Civils' : 'Civil Trials',
        value: stats.civil_trials || 0,
        icon: Gavel,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        link: '/trials?type=civil'
      },
      {
        id: 'civil-complaints',
        category: 'civil',
        title: language === 'fr' ? 'Plaintes' : 'Complaints',
        value: stats.total_complaints || 0,
        icon: FileText,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        link: '/complaints'
      },
      // Military Justice Widgets
      {
        id: 'military-cases',
        category: 'military',
        title: language === 'fr' ? 'Dossiers Militaires' : 'Military Cases',
        value: stats.military_cases || 0,
        icon: Swords,
        color: 'text-red-600',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        link: '/cases?type=military'
      },
      {
        id: 'military-trials',
        category: 'military',
        title: language === 'fr' ? 'Procès Militaires' : 'Military Trials',
        value: stats.military_trials || 0,
        icon: Gavel,
        color: 'text-red-600',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        link: '/trials?type=military'
      },
      // Prison Widgets
      {
        id: 'total-prisoners',
        category: 'prison',
        title: language === 'fr' ? 'Total Détenus' : 'Total Inmates',
        value: stats.total_prisoners || 0,
        icon: Users,
        color: 'text-orange-600',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        link: '/prisoners'
      },
      {
        id: 'preventive-prisoners',
        category: 'prison',
        title: language === 'fr' ? 'Préventifs' : 'Preventive',
        value: stats.preventive_prisoners || 0,
        icon: Clock,
        color: 'text-orange-600',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        link: '/prisoners?status=preventive'
      },
      {
        id: 'condemned-prisoners',
        category: 'prison',
        title: language === 'fr' ? 'Condamnés' : 'Condemned',
        value: stats.condemned_prisoners || 0,
        icon: Lock,
        color: 'text-orange-600',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        link: '/prisoners?status=condemned'
      },
      // Finance Widgets
      {
        id: 'payments',
        category: 'finance',
        title: language === 'fr' ? 'Paiements' : 'Payments',
        value: stats.totalPayments || stats.total_payments || 0,
        icon: CreditCard,
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
        link: '/payments'
      },
      {
        id: 'cash-register',
        category: 'finance',
        title: language === 'fr' ? 'Caisse' : 'Cash Register',
        value: stats.cash_balance || 0,
        icon: Calculator,
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
        link: '/cash-register'
      },
      {
        id: 'pending-payments',
        category: 'finance',
        title: language === 'fr' ? 'En Attente' : 'Pending',
        value: stats.pending_payments || 0,
        icon: DollarSign,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        link: '/payments?status=PENDING'
      },
      // Border Widgets
      {
        id: 'border-alerts',
        category: 'border',
        title: language === 'fr' ? 'Alertes Frontières' : 'Border Alerts',
        value: stats.border_alerts || 0,
        icon: AlertTriangle,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        link: '/border-control'
      },
      {
        id: 'fugitives',
        category: 'border',
        title: language === 'fr' ? 'Fugitifs' : 'Fugitives',
        value: stats.fugitives || 0,
        icon: Shield,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        link: '/border-control?type=fugitives'
      },
      // Library Widgets
      {
        id: 'legal-docs',
        category: 'library',
        title: language === 'fr' ? 'Textes de Loi' : 'Legal Texts',
        value: stats.legal_texts || 0,
        icon: BookOpen,
        color: 'text-green-600',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        link: '/legal-library'
      },
      {
        id: 'legal-offenses',
        category: 'library',
        title: language === 'fr' ? 'Infractions' : 'Offenses',
        value: stats.legal_offenses || 0,
        icon: AlertTriangle,
        color: 'text-red-600',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        link: '/legal-library'
      },
      // General Widgets (always visible)
      {
        id: 'notifications',
        category: 'general',
        title: language === 'fr' ? 'Notifications' : 'Notifications',
        value: stats.notifications || 0,
        icon: Bell,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        link: '/notifications'
      }
    ];

    // Step 1: Filter by module access - only show widgets the user can access
    const accessibleWidgets = allWidgets.filter(w => {
      const requiredModule = categoryModuleMap[w.category];
      return hasAccess(requiredModule);
    });

    // Step 2: Filter by active thematic filter
    if (activeFilter === 'all') {
      return accessibleWidgets;
    }
    return accessibleWidgets.filter(w => 
      w.category === activeFilter || w.category === 'general'
    );
  };

  const filteredWidgets = getFilteredWidgets();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="home-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <LayoutDashboard className="w-7 h-7 text-primary" />
              {language === 'fr' ? 'Tableau de Bord' : 'Dashboard'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'fr' 
                ? `Bienvenue, ${user?.name || 'Utilisateur'}`
                : `Welcome, ${user?.name || 'User'}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {user?.role?.replace(/_/g, ' ')}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {user?.province || 'NATIONAL'}
            </Badge>
          </div>
        </div>

        {/* Zone 1: Thematic Filters */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-primary" />
              {language === 'fr' ? 'Filtres Thématiques' : 'Thematic Filters'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid grid-cols-2 sm:grid-cols-3 ${accessibleFilters.length <= 4 ? 'md:grid-cols-4' : accessibleFilters.length <= 6 ? 'md:grid-cols-6' : 'md:grid-cols-7'} gap-3`}>
              {accessibleFilters.map((filter) => {
                const Icon = filter.icon;
                const isActive = activeFilter === filter.id;
                return (
                  <button
                    key={filter.id}
                    onClick={() => setActiveFilter(filter.id)}
                    className={`
                      relative p-4 rounded-xl transition-all duration-200
                      ${isActive 
                        ? `${filter.color} text-white shadow-lg scale-105` 
                        : 'bg-muted hover:bg-muted/80 hover:scale-102'}
                    `}
                    data-testid={`filter-${filter.id}`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Icon className={`w-6 h-6 ${isActive ? '' : 'text-muted-foreground'}`} />
                      <span className="text-xs font-medium text-center">
                        {filter.label[language]}
                      </span>
                    </div>
                    {isActive && (
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-white" />
                    )}
                  </button>
                );
              })}
            </div>
            {activeFilter !== 'all' && (
              <p className="text-sm text-muted-foreground mt-3 text-center">
                {THEMATIC_FILTERS.find(f => f.id === activeFilter)?.description[language]}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Zone 2: Filtered Widgets */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {loading ? (
            Array(8).fill(0).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-4">
                  <div className="h-16 bg-muted rounded"></div>
                </CardContent>
              </Card>
            ))
          ) : (
            filteredWidgets.map((widget) => {
              const Icon = widget.icon;
              return (
                <Link key={widget.id} to={widget.link}>
                  <Card className="hover:shadow-lg transition-all duration-200 hover:scale-105 h-full cursor-pointer">
                    <CardContent className="p-4">
                      <div className={`w-10 h-10 rounded-lg ${widget.bgColor} flex items-center justify-center mb-3`}>
                        <Icon className={`w-5 h-5 ${widget.color}`} />
                      </div>
                      <p className="text-2xl font-bold">{widget.value}</p>
                      <p className="text-xs text-muted-foreground">{widget.title}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })
          )}
        </div>

        {/* Quick Actions - Filtered by module access */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              {language === 'fr' ? 'Actions Rapides' : 'Quick Actions'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {hasAccess('justice_civile') && (
                <Link to="/cases">
                  <Button variant="outline" className="w-full justify-start gap-2 h-auto py-3" data-testid="quick-action-cases">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    <span className="text-xs">{language === 'fr' ? 'Nouveau Dossier' : 'New Case'}</span>
                  </Button>
                </Link>
              )}
              {hasAccess('justice_civile') && (
                <Link to="/complaints">
                  <Button variant="outline" className="w-full justify-start gap-2 h-auto py-3" data-testid="quick-action-complaints">
                    <FileText className="w-4 h-4 text-orange-600" />
                    <span className="text-xs">{language === 'fr' ? 'Nouvelle Plainte' : 'New Complaint'}</span>
                  </Button>
                </Link>
              )}
              {hasAccess('prisons') && (
                <Link to="/prisoners">
                  <Button variant="outline" className="w-full justify-start gap-2 h-auto py-3" data-testid="quick-action-prisoners">
                    <Users className="w-4 h-4 text-purple-600" />
                    <span className="text-xs">{language === 'fr' ? 'Détenus' : 'Inmates'}</span>
                  </Button>
                </Link>
              )}
              {hasAccess('finances') && (
                <Link to="/cash-register">
                  <Button variant="outline" className="w-full justify-start gap-2 h-auto py-3" data-testid="quick-action-cash">
                    <Calculator className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs">{language === 'fr' ? 'Caisse' : 'Cash Register'}</span>
                  </Button>
                </Link>
              )}
              <Link to="/map">
                <Button variant="outline" className="w-full justify-start gap-2 h-auto py-3" data-testid="quick-action-map">
                  <MapPin className="w-4 h-4 text-green-600" />
                  <span className="text-xs">{language === 'fr' ? 'Carte' : 'Map'}</span>
                </Button>
              </Link>
              {/* Call Center Quick Action */}
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 h-auto py-3 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                onClick={() => setShowCallCenter(true)}
                data-testid="quick-call-center"
              >
                <Phone className="w-4 h-4 text-red-600" />
                <span className="text-xs">{language === 'fr' ? 'Centre d\'Appel' : 'Call Center'}</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Call Center Card - Prominent */}
        <Card className="border-2 border-red-200 dark:border-red-800 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center animate-pulse shadow-lg">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-red-700 dark:text-red-400">
                    {language === 'fr' ? 'Centre d\'Appel Judiciaire' : 'Judicial Call Center'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {language === 'fr' 
                      ? 'Contactez directement les juridictions - Appel, WhatsApp, Messenger'
                      : 'Contact jurisdictions directly - Call, WhatsApp, Messenger'}
                  </p>
                </div>
              </div>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white gap-2 px-6"
                onClick={() => setShowCallCenter(true)}
                data-testid="open-call-center"
              >
                <Phone className="w-5 h-5" />
                {language === 'fr' ? 'Ouvrir' : 'Open'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* User Info + Login History */}
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <UserCircle className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge>{user?.role?.replace(/_/g, ' ')}</Badge>
                <Badge variant="outline">{user?.province || 'NATIONAL'}</Badge>
                <Badge variant="secondary">{user?.clearance_level || 'STANDARD'}</Badge>
              </div>
            </div>

            {/* Login History (7 dernières connexions) */}
            {loginHistory.length > 0 && (
              <div className="pt-3 border-t">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2" data-testid="login-history-title">
                  <Shield className="w-4 h-4 text-amber-600" />
                  {language === 'fr' ? '7 dernières connexions' : 'Last 7 logins'}
                </h4>
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                  {loginHistory.map((entry, idx) => {
                    const DeviceIcon = entry.device_type === 'Telephone' ? Smartphone : entry.device_type === 'Tablette' ? Tablet : Monitor;
                    const isCurrentSession = idx === 0;
                    const isSuspicious = entry.suspicious;
                    return (
                      <div
                        key={entry.login_id || idx}
                        className={`flex items-center gap-3 px-3 py-2 rounded text-xs ${
                          isSuspicious ? 'bg-red-100/50 dark:bg-red-900/20 border border-red-300 dark:border-red-800' :
                          isCurrentSession ? 'bg-green-100/50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 
                          'bg-background/50 border border-border/50'
                        }`}
                        data-testid={`login-entry-${idx}`}
                      >
                        <DeviceIcon className={`w-4 h-4 shrink-0 ${isSuspicious ? 'text-red-600' : isCurrentSession ? 'text-green-600' : 'text-muted-foreground'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{entry.browser} / {entry.os}</span>
                            {isCurrentSession && <Badge className="bg-green-500 text-white text-[9px] px-1">{language === 'fr' ? 'Actuelle' : 'Current'}</Badge>}
                            {isSuspicious && <Badge className="bg-red-500 text-white text-[9px] px-1" data-testid={`suspicious-badge-${idx}`}>{language === 'fr' ? 'Suspecte' : 'Suspicious'}</Badge>}
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground mt-0.5 flex-wrap">
                            <Wifi className="w-3 h-3" />
                            <span>{entry.ip_address}</span>
                            <span>|</span>
                            <Clock className="w-3 h-3" />
                            <span>{new Date(entry.login_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            <span>|</span>
                            <span>{entry.device_type}</span>
                          </div>
                          {isSuspicious && entry.alerts && (
                            <div className="text-red-600 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              <span>{entry.alerts.join(', ')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Call Center Floating Button */}
      <Button
        onClick={() => setShowCallCenter(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-red-600 hover:bg-red-700 z-40 animate-pulse"
        size="icon"
        data-testid="call-center-fab"
      >
        <Phone className="w-6 h-6" />
      </Button>

      {/* Police Assistant Button - Move up when both present */}
      {isPoliceRole && (
        <Button
          onClick={() => setShowPoliceAssistant(true)}
          className="fixed bottom-24 right-6 h-14 w-14 rounded-full shadow-lg bg-orange-500 hover:bg-orange-600 z-40"
          size="icon"
          data-testid="police-assistant-btn"
        >
          <UserSearch className="w-6 h-6" />
        </Button>
      )}

      {/* Call Center Modal */}
      <CallCenterWidget
        isOpen={showCallCenter}
        onClose={() => setShowCallCenter(false)}
      />

      {/* Police Assistant Modal */}
      <PoliceAssistant 
        isOpen={showPoliceAssistant}
        onClose={() => setShowPoliceAssistant(false)}
        onActionComplete={() => loadStats()}
      />
    </DashboardLayout>
  );
};

export default HomePage;
